import { DateRange, PeriodPreset } from "@/store/statisticsPeriodStore";

const DAY_MS = 24 * 60 * 60 * 1000;

export const MONTH_NAMES = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];
const MONTH_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];
const MONTH_SHORT = [
  "янв",
  "фев",
  "мар",
  "апр",
  "мая",
  "июн",
  "июл",
  "авг",
  "сен",
  "окт",
  "ноя",
  "дек",
];

export const PERIOD_PRESETS: {
  id: PeriodPreset;
  name: string;
  days: number;
}[] = [
  { id: "7", name: "7 дней", days: 7 },
  { id: "30", name: "30 дней", days: 30 },
  { id: "90", name: "90 дней", days: 90 },
  { id: "all", name: "Всё время", days: 0 },
];

export const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const addDays = (date: Date, days: number) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

export const isSameDay = (a: Date, b: Date) => a.getTime() === b.getTime();

// количество дней в диапазоне включительно
export const daysInRange = (from: Date, to: Date) =>
  Math.round((to.getTime() - from.getTime()) / DAY_MS) + 1;

export const pluralDays = (n: number) => {
  const a = n % 10;
  const b = n % 100;
  if (b >= 11 && b <= 14) return "дней";
  if (a === 1) return "день";
  if (a >= 2 && a <= 4) return "дня";
  return "дней";
};

export const getPresetRange = (
  preset: PeriodPreset,
  today: Date,
  firstDate?: Date
): DateRange | null => {
  const days = PERIOD_PRESETS.find((p) => p.id === preset)?.days ?? 0;

  if (preset === "all") {
    return firstDate ? { from: firstDate, to: today } : null;
  }

  const from = addDays(today, -days + 1);
  return {
    from: firstDate && firstDate > from ? firstDate : from,
    to: today,
  };
};

// «2026-09-21» — формат дат для API
export const formatApiDate = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

// «2026-09-21» из API → локальная дата; null, если формат не тот
export const parseApiDate = (value?: string | null): Date | null => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  if (!parts) return null;
  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
};

// «11 авг»
export const formatTiny = (date: Date) =>
  `${date.getDate()} ${MONTH_SHORT[date.getMonth()]}`;

// «11 августа»
export const formatDayMonth = (date: Date) =>
  `${date.getDate()} ${MONTH_GENITIVE[date.getMonth()]}`;

// «11 августа 2026»
export const formatFull = (date: Date) =>
  `${date.getDate()} ${MONTH_GENITIVE[date.getMonth()]} ${date.getFullYear()}`;

// «11 — 30 августа 2026», «11 августа — 9 сентября 2026»
export const formatRange = ({ from, to }: DateRange) => {
  if (from.getFullYear() !== to.getFullYear()) {
    return `${formatFull(from)} — ${formatFull(to)}`;
  }
  const start =
    from.getMonth() === to.getMonth()
      ? `${from.getDate()}`
      : `${from.getDate()} ${MONTH_GENITIVE[from.getMonth()]}`;
  return `${start} — ${formatFull(to)}`;
};

// «11 — 30 авг», «11 авг — 9 сен»
export const formatRangeTiny = ({ from, to }: DateRange) =>
  from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()
    ? `${from.getDate()} — ${formatTiny(to)}`
    : `${formatTiny(from)} — ${formatTiny(to)}`;

export const formatCustomRange = ({ from, to }: DateRange) =>
  `${formatTiny(from)} — ${formatTiny(to)}`;

export const formatAllTimeShort = (firstDate?: Date) =>
  firstDate ? `всё время · с ${formatTiny(firstDate)}` : "Всё время";
